import { useEffect, useMemo, useState } from 'react';
import { FileText, Folder, X } from 'lucide-react';
import { useVault } from '../../../core/lib/vault';
import { listDecryptedCustomers, listDecryptedDocuments } from '../sync';

interface SearchHit {
  type: 'customer' | 'document';
  customerId: string;
  customerName: string;
  documentId?: string;
  parentId?: string | null;
  title: string;
  snippet?: string;
}

export function GlobalSearch({
  onClose,
  onSelect,
}: {
  onClose: () => void;
  onSelect: (customerId: string, pageId: string | null, tabId: string | null) => void;
}) {
  const vaultKey = useVault((s) => s.vaultKey);
  const [q, setQ] = useState('');
  const [allHits, setAllHits] = useState<SearchHit[]>([]);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!vaultKey) return;
      const customers = await listDecryptedCustomers(vaultKey);
      const all: SearchHit[] = [];
      for (const c of customers) {
        all.push({ type: 'customer', customerId: c.id, customerName: c.name, title: c.name });
        const docs = await listDecryptedDocuments(c.id, vaultKey);
        for (const d of docs) {
          all.push({
            type: 'document',
            customerId: c.id,
            customerName: c.name,
            documentId: d.id,
            parentId: d.parentId,
            title: d.title || 'Untitled',
            snippet: firstLine(d.markdown),
          });
        }
      }
      if (active) setAllHits(all);
    })();
    return () => {
      active = false;
    };
  }, [vaultKey]);

  const filtered = useMemo(() => {
    if (!q.trim()) return allHits.slice(0, 30);
    const lc = q.toLowerCase();
    return allHits
      .filter(
        (h) =>
          h.title.toLowerCase().includes(lc) ||
          h.customerName.toLowerCase().includes(lc) ||
          (h.snippet ?? '').toLowerCase().includes(lc),
      )
      .slice(0, 50);
  }, [q, allHits]);

  function activate(h: SearchHit) {
    if (h.type === 'customer') {
      onSelect(h.customerId, null, null);
    } else if (h.parentId) {
      // A tab: open its parent page and focus the tab.
      onSelect(h.customerId, h.parentId, h.documentId ?? null);
    } else {
      onSelect(h.customerId, h.documentId ?? null, null);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-end md:items-start justify-center md:pt-24 z-50">
      <div className="card w-full max-h-[85dvh] md:max-h-none md:max-w-2xl md:mx-4 rounded-b-none md:rounded-b-lg overflow-hidden">
        <div className="flex items-center border-b border-ink-800 px-3 py-2">
          <input
            autoFocus
            className="flex-1 bg-transparent px-2 py-1 focus:outline-none"
            placeholder="Search everything…"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setActiveIdx(0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') onClose();
              if (e.key === 'ArrowDown') setActiveIdx((i) => Math.min(i + 1, filtered.length - 1));
              if (e.key === 'ArrowUp') setActiveIdx((i) => Math.max(i - 1, 0));
              if (e.key === 'Enter' && filtered[activeIdx]) activate(filtered[activeIdx]!);
            }}
          />
          <button onClick={onClose} className="btn-ghost !p-1">
            <X size={14} />
          </button>
        </div>
        <ul className="max-h-96 overflow-y-auto">
          {filtered.length === 0 && (
            <li className="px-4 py-6 text-center text-sm text-ink-500">No results.</li>
          )}
          {filtered.map((h, i) => (
            <li
              key={`${h.type}-${h.documentId ?? h.customerId}`}
              className={`px-3 py-2 flex items-center gap-2 cursor-pointer ${
                i === activeIdx ? 'bg-ink-800' : 'hover:bg-ink-800/60'
              }`}
              onMouseEnter={() => setActiveIdx(i)}
              onClick={() => activate(h)}
            >
              {h.type === 'customer' ? (
                <Folder size={14} className="text-ink-400" />
              ) : (
                <FileText size={14} className="text-ink-400" />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">
                  {h.title}
                  {h.type === 'document' && (
                    <span className="text-ink-500"> — {h.customerName}</span>
                  )}
                </div>
                {h.snippet && <div className="text-xs text-ink-500 truncate">{h.snippet}</div>}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function firstLine(md: string): string {
  const line = md.split(/\r?\n/).find((l) => l.trim())?.trim() ?? '';
  return line.length > 160 ? line.slice(0, 160) + '…' : line;
}
